import { useEffect, useState } from 'react';
import {
  Stack,
  CommandBar,
  DetailsList,
  IconButton,
  SelectionMode,
} from '@fluentui/react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Field,
  Input,
  Dropdown,
  Option,
  Button,
  Subtitle2Stronger,
  Caption1,
  Divider,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge,
  ToggleButton,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  ArrowUpRegular,
  ArrowDownRegular,
  DeleteRegular,
  AddRegular,
  BorderAllRegular,
} from '@fluentui/react-icons';
import type {
  CardSlotAlign,
  CardSlotDisplay,
  CardSlotFontSize,
  CardStaticFieldKey,
  CardStaticFieldPosition,
  ICardSlot,
  ICardStaticFieldBinding,
  IMetadata,
} from '../../services/metadataService';
import CardPreview from './CardPreview';

interface ICardDesignerPanelProps {
  disabled?: boolean;
  metadata?: IMetadata;
  entityType?: string;
  setMetadata: (metadata: IMetadata) => void;
  setMetadataStr: (metadataStr: string) => void;
}

const CARD_DISPLAY_OPTIONS = ['block', 'inline'] as const;
const CARD_ALIGN_OPTIONS = ['left', 'center', 'right'] as const;
const CARD_FONT_SIZE_OPTIONS = ['xs', 'sm', 'md', 'lg'] as const;

const CARD_SLOT_SUPPORTED_FIELD_TYPES = new Set([
  'String',
  'Date',
  'Number',
  'Address',
  'Lookup',
  'AutoComplete',
  'UserSelector',
]);

const CARD_STATIC_FIELD_OPTIONS: { key: CardStaticFieldKey; label: string }[] =
  [
    { key: 'id', label: 'Id' },
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status' },
    { key: 'entityTypeName', label: 'Entity type name' },
  ];

// Unified field representation for slot assignment
interface ISlotField {
  id: string; // unique identifier for this field-in-slot
  fieldKey: string; // either static field key or custom field name
  isStatic: boolean;
  order: number;
  label: string; // display label
}

const CardDesignerPanel = (props: ICardDesignerPanelProps) => {
  const {
    metadata,
    setMetadata,
    setMetadataStr,
    entityType,
    disabled = false,
  } = props;

  const availableStaticFieldOptions = CARD_STATIC_FIELD_OPTIONS.filter(
    (option) => {
      if (option.key === 'name') {
        return entityType === 'Asset';
      }
      if (option.key === 'description') {
        return entityType === 'Work' || entityType === 'Order';
      }
      return true;
    }
  );

  const [isSlotEditorPanelOpen, setIsSlotEditorPanelOpen] = useState(false);
  const showSlotEditorPanel = () => setIsSlotEditorPanelOpen(true);
  const hideSlotEditorPanel = () => setIsSlotEditorPanelOpen(false);

  const [slots, setSlots] = useState<ICardSlot[]>([]);
  const [currentSlot, setCurrentSlot] = useState<ICardSlot | undefined>();
  const [selectedRow, setSelectedRow] = useState<number>(0);
  const [slotError, setSlotError] = useState<string>();

  // Fields assigned to the current slot being edited
  const [slotFields, setSlotFields] = useState<ISlotField[]>([]);

  const [showSlotBorders, setShowSlotBorders] = useState(true);
  const [showSlotNames, setShowSlotNames] = useState(true);

  const [staticFieldBindings, setStaticFieldBindings] = useState<
    ICardStaticFieldBinding[]
  >([]);

  useEffect(() => {
    setSlots(metadata?.cardLayout?.slots ?? []);
    setStaticFieldBindings(metadata?.cardLayout?.staticFields ?? []);
    setSlotError(undefined);
  }, [metadata]);

  // Build the list of fields for the current slot
  const buildSlotFieldsList = (slotId: string): ISlotField[] => {
    const fields: ISlotField[] = [];

    // Add static fields bound to this slot
    const staticBindings = (metadata?.cardLayout?.staticFields ?? []).filter(
      (binding) => binding.slotId === slotId
    );
    staticBindings.forEach((binding) => {
      const staticFieldOption = CARD_STATIC_FIELD_OPTIONS.find(
        (opt) => opt.key === binding.key
      );
      if (staticFieldOption) {
        fields.push({
          id: `static_${binding.key}`,
          fieldKey: binding.key,
          isStatic: true,
          order:
            binding.position === 'start'
              ? -1000 + fields.length
              : 1000 + fields.length,
          label: staticFieldOption.label,
        });
      }
    });

    // Add custom fields bound to this slot
    const customFields = (metadata?.fields ?? []).filter(
      (field) => field.cardSlotId === slotId
    );
    customFields.forEach((field) => {
      fields.push({
        id: `custom_${field.name}`,
        fieldKey: field.name,
        isStatic: false,
        order: field.cardSlotOrder ?? 0,
        label: field.label || field.name,
      });
    });

    // Sort by order
    return fields.sort((a, b) => a.order - b.order);
  };

  const getNextSlotOrder = (): number => {
    if (slots.length === 0) return 1;
    return Math.max(...slots.map((s) => s.order || 0)) + 1;
  };

  const normalizeSlotId = (label: string): string =>
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);

  const toOptionalText = (value: string | undefined): string | undefined => {
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const handleAddSlot = () => {
    setSelectedRow(0);
    setCurrentSlot(undefined);
    setSlotError(undefined);
    setSlotFields([]);
    showSlotEditorPanel();
  };

  const handleEditSlot = (slot: ICardSlot, index: number) => {
    setSelectedRow(index + 1); // +1 to distinguish from "add new" (0)
    setCurrentSlot(slot);
    setSlotError(undefined);
    setSlotFields(buildSlotFieldsList(slot.id));
    showSlotEditorPanel();
  };

  const handleDeleteSlot = (index: number) => {
    const slotToDelete = slots[index];
    const newSlots = slots.filter((_, i) => i !== index);

    // Remove static field bindings for this slot
    const newStaticBindings = staticFieldBindings.filter(
      (binding) => binding.slotId !== slotToDelete.id
    );

    // Update metadata to clear cardSlotId for custom fields in this slot
    if (metadata) {
      const updatedFields = (metadata.fields ?? []).map((field) => {
        if (field.cardSlotId === slotToDelete.id) {
          const { cardSlotId, cardSlotOrder, ...rest } = field;
          return rest;
        }
        return field;
      });

      const nextMetadata: IMetadata = {
        ...metadata,
        fields: updatedFields,
        cardLayout: {
          ...metadata.cardLayout,
          slots: newSlots,
          staticFields:
            newStaticBindings.length > 0 ? newStaticBindings : undefined,
        },
      };

      setMetadata(nextMetadata);
      setMetadataStr(JSON.stringify(nextMetadata, null, 2));
      setSlots(newSlots);
      setStaticFieldBindings(newStaticBindings);
    }
  };

  const handleMoveSlotUp = (index: number) => {
    if (index === 0 || !metadata) return;

    const newSlots = [...slots];
    // Swap with previous slot
    [newSlots[index - 1], newSlots[index]] = [
      newSlots[index],
      newSlots[index - 1],
    ];

    // Reassign order based on position
    newSlots.forEach((slot, idx) => {
      slot.order = idx + 1;
    });

    const nextMetadata: IMetadata = {
      ...metadata,
      cardLayout: {
        ...metadata.cardLayout,
        slots: newSlots,
      },
    };

    setMetadata(nextMetadata);
    setMetadataStr(JSON.stringify(nextMetadata, null, 2));
    setSlots(newSlots);
  };

  const handleMoveSlotDown = (index: number) => {
    if (index === slots.length - 1 || !metadata) return;

    const newSlots = [...slots];
    // Swap with next slot
    [newSlots[index], newSlots[index + 1]] = [
      newSlots[index + 1],
      newSlots[index],
    ];

    // Reassign order based on position
    newSlots.forEach((slot, idx) => {
      slot.order = idx + 1;
    });

    const nextMetadata: IMetadata = {
      ...metadata,
      cardLayout: {
        ...metadata.cardLayout,
        slots: newSlots,
      },
    };

    setMetadata(nextMetadata);
    setMetadataStr(JSON.stringify(nextMetadata, null, 2));
    setSlots(newSlots);
  };

  const handleAddFieldToSlot = (fieldKey: string, isStatic: boolean) => {
    const label = isStatic
      ? CARD_STATIC_FIELD_OPTIONS.find((opt) => opt.key === fieldKey)?.label ??
        fieldKey
      : (metadata?.fields ?? []).find((f) => f.name === fieldKey)?.label ??
        fieldKey;

    const nextOrder =
      slotFields.length > 0
        ? Math.max(...slotFields.map((f) => f.order)) + 1
        : 1;

    const newField: ISlotField = {
      id: isStatic ? `static_${fieldKey}` : `custom_${fieldKey}`,
      fieldKey,
      isStatic,
      order: nextOrder,
      label,
    };

    setSlotFields([...slotFields, newField]);
  };

  const handleRemoveFieldFromSlot = (fieldId: string) => {
    setSlotFields(slotFields.filter((f) => f.id !== fieldId));
  };

  const handleMoveFieldUp = (index: number) => {
    if (index === 0) return;
    const newFields = [...slotFields];
    [newFields[index - 1], newFields[index]] = [
      newFields[index],
      newFields[index - 1],
    ];
    // Reassign orders based on position
    newFields.forEach((field, idx) => {
      field.order = idx + 1;
    });
    setSlotFields(newFields);
  };

  const handleMoveFieldDown = (index: number) => {
    if (index === slotFields.length - 1) return;
    const newFields = [...slotFields];
    [newFields[index], newFields[index + 1]] = [
      newFields[index + 1],
      newFields[index],
    ];
    // Reassign orders based on position
    newFields.forEach((field, idx) => {
      field.order = idx + 1;
    });
    setSlotFields(newFields);
  };

  const handleSaveSlot = () => {
    if (!currentSlot) {
      setSlotError('Slot data is missing.');
      return;
    }

    const label = currentSlot.label.trim();
    if (!label) {
      setSlotError('Slot label is required.');
      return;
    }

    const id = (currentSlot.id || normalizeSlotId(label)).trim();
    if (!id) {
      setSlotError('Slot id is required.');
      return;
    }

    // Check for duplicate ids (excluding the current slot if editing)
    const isDuplicate = slots.some(
      (slot, idx) => slot.id === id && idx !== selectedRow - 1
    );
    if (isDuplicate) {
      setSlotError(`Slot id "${id}" already exists.`);
      return;
    }

    const normalizedSlot: ICardSlot = {
      id,
      label,
      order: currentSlot.order,
      style: {
        display: currentSlot.style?.display ?? 'block',
        align: currentSlot.style?.align ?? 'left',
        fontSize: currentSlot.style?.fontSize ?? 'md',
        color: toOptionalText(currentSlot.style?.color),
      },
    };

    const newSlots = [...slots];
    if (selectedRow === 0) {
      // Adding new slot
      newSlots.push(normalizedSlot);
    } else {
      // Editing existing slot
      newSlots.splice(selectedRow - 1, 1, normalizedSlot);
    }

    // Save slot and field assignments together
    saveSlotWithFields(newSlots, id, slotFields);
    hideSlotEditorPanel();
  };

  const saveSlotWithFields = (
    newSlots: ICardSlot[],
    slotId: string,
    fields: ISlotField[]
  ) => {
    if (!metadata) {
      setSlotError('Metadata is not loaded.');
      return;
    }

    const sorted = sortSlots(newSlots);

    // Build new static field bindings
    const otherSlotStaticBindings = staticFieldBindings.filter(
      (binding) => binding.slotId !== slotId
    );

    const currentSlotStaticBindings: ICardStaticFieldBinding[] = fields
      .filter((f) => f.isStatic)
      .map((f) => ({
        key: f.fieldKey as CardStaticFieldKey,
        slotId,
        position: 'start' as CardStaticFieldPosition, // All fields in order, no start/end distinction
      }));

    const allStaticBindings = [
      ...otherSlotStaticBindings,
      ...currentSlotStaticBindings,
    ];

    // Update custom fields
    const updatedFields = (metadata.fields ?? []).map((field) => {
      // If this field is in the current slot's field list
      const slotField = fields.find(
        (f) => !f.isStatic && f.fieldKey === field.name
      );

      if (slotField) {
        // Assign to this slot
        return {
          ...field,
          cardSlotId: slotId,
          cardSlotOrder: slotField.order,
        };
      } else if (field.cardSlotId === slotId) {
        // Field was in this slot but is no longer - remove assignment
        const { cardSlotId, cardSlotOrder, ...rest } = field;
        return rest;
      }

      return field;
    });

    const allowedSlotIds = new Set(sorted.map((s) => s.id));
    const allowedStaticKeys = new Set(
      availableStaticFieldOptions.map((option) => option.key)
    );
    const filteredBindings = allStaticBindings.filter(
      (b) => allowedSlotIds.has(b.slotId) && allowedStaticKeys.has(b.key)
    );
    const sortedBindings = sortStaticFieldBindings(filteredBindings);

    const nextMetadata: IMetadata = {
      ...metadata,
      fields: updatedFields,
      cardLayout: {
        ...metadata.cardLayout,
        slots: sorted,
        staticFields: sortedBindings.length > 0 ? sortedBindings : undefined,
      },
    };

    setMetadata(nextMetadata);
    setMetadataStr(JSON.stringify(nextMetadata, null, 2));
    setSlots(sorted);
    setStaticFieldBindings(sortedBindings);
  };

  const sortSlots = (s: ICardSlot[]): ICardSlot[] =>
    [...s].sort((a, b) => a.order - b.order);

  const sortStaticFieldBindings = (
    bindings: ICardStaticFieldBinding[]
  ): ICardStaticFieldBinding[] =>
    [...bindings].sort((a, b) => {
      const slotA = slots.find((s) => s.id === a.slotId)?.order ?? 0;
      const slotB = slots.find((s) => s.id === b.slotId)?.order ?? 0;
      if (slotA !== slotB) return slotA - slotB;
      // start before end
      if (a.position !== b.position) return a.position === 'start' ? -1 : 1;
      return a.key.localeCompare(b.key);
    });

  const getFieldCountForSlot = (slotId: string): number => {
    const staticCount = (metadata?.cardLayout?.staticFields ?? []).filter(
      (binding) => binding.slotId === slotId
    ).length;
    const customCount = (metadata?.fields ?? []).filter(
      (field) => field.cardSlotId === slotId
    ).length;
    return staticCount + customCount;
  };

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Subtitle2Stronger>Card Slots</Subtitle2Stronger>
      <CommandBar
        items={[
          {
            key: 'AddSlot',
            text: 'Add',
            disabled: disabled,
            iconProps: { iconName: 'Add' },
            onClick: handleAddSlot,
          },
        ]}
      />

      <DetailsList
        columns={[
          {
            key: 'label',
            name: 'Label',
            fieldName: 'label',
            minWidth: 120,
          },
          {
            key: 'id',
            name: 'Id',
            fieldName: 'id',
            minWidth: 120,
          },
          {
            key: 'fields',
            name: 'Fields',
            minWidth: 80,
            onRender: (item: ICardSlot) => (
              <span>{getFieldCountForSlot(item.id)} field(s)</span>
            ),
          },
          {
            key: 'action',
            name: 'Action',
            minWidth: 140,
            onRender: (item: ICardSlot, index?: number) => (
              <Stack horizontal tokens={{ childrenGap: 4 }}>
                <IconButton
                  iconProps={{ iconName: 'Up' }}
                  disabled={index === 0}
                  onClick={() => {
                    handleMoveSlotUp(index ?? 0);
                  }}
                  title="Move up"
                />
                <IconButton
                  iconProps={{ iconName: 'Down' }}
                  disabled={index === slots.length - 1}
                  onClick={() => {
                    handleMoveSlotDown(index ?? 0);
                  }}
                  title="Move down"
                />
                <IconButton
                  iconProps={{ iconName: 'Edit' }}
                  onClick={() => {
                    handleEditSlot(item, index ?? 0);
                  }}
                  title="Edit"
                />
                <IconButton
                  iconProps={{ iconName: 'Delete' }}
                  onClick={() => {
                    handleDeleteSlot(index ?? 0);
                  }}
                  title="Delete"
                />
              </Stack>
            ),
          },
        ]}
        items={slots.map((slot, index) => ({ ...slot, index }))}
        selectionMode={SelectionMode.none}
      />

      <Divider />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Subtitle2Stronger>Preview</Subtitle2Stronger>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ToggleButton
            size="small"
            checked={!showSlotBorders}
            icon={<BorderAllRegular />}
            onClick={() => setShowSlotBorders((prev) => !prev)}
          >
            Hide slot dividers
          </ToggleButton>
          <ToggleButton
            size="small"
            checked={!showSlotNames}
            onClick={() => setShowSlotNames((prev) => !prev)}
          >
            Hide slot names
          </ToggleButton>
        </div>
      </div>
      <CardPreview
        metadata={metadata}
        showBorders={showSlotBorders}
        showSlotNames={showSlotNames}
      />

      <OverlayDrawer
        open={isSlotEditorPanelOpen}
        onOpenChange={(_, { open }) => {
          if (!open) hideSlotEditorPanel();
        }}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={hideSlotEditorPanel}
              />
            }
          >
            {selectedRow === 0 ? 'New Card Slot' : 'Edit Card Slot'}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              paddingTop: 8,
            }}
          >
            {slotError && (
              <div style={{ color: '#d13438', fontSize: 12 }}>{slotError}</div>
            )}

            <Subtitle2Stronger>Slot Properties</Subtitle2Stronger>

            <Field label="Label">
              <Input
                value={currentSlot?.label ?? ''}
                onChange={(_, data) => {
                  setCurrentSlot({
                    id: currentSlot?.id ?? '',
                    label: data.value,
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: currentSlot?.style,
                  });
                }}
              />
            </Field>

            <Field label="Id (optional)">
              <Input
                value={currentSlot?.id ?? ''}
                placeholder="Auto-generated from label"
                onChange={(_, data) => {
                  setCurrentSlot({
                    id: data.value,
                    label: currentSlot?.label ?? '',
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: currentSlot?.style,
                  });
                }}
              />
            </Field>

            <Field label="Display">
              <Dropdown
                value={currentSlot?.style?.display ?? 'block'}
                selectedOptions={[currentSlot?.style?.display ?? 'block']}
                onOptionSelect={(_ev, data) => {
                  setCurrentSlot({
                    id: currentSlot?.id ?? '',
                    label: currentSlot?.label ?? '',
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: {
                      ...currentSlot?.style,
                      display: (data.optionValue as CardSlotDisplay) ?? 'block',
                    },
                  });
                }}
              >
                {CARD_DISPLAY_OPTIONS.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field label="Align">
              <Dropdown
                value={currentSlot?.style?.align ?? 'left'}
                selectedOptions={[currentSlot?.style?.align ?? 'left']}
                onOptionSelect={(_ev, data) => {
                  setCurrentSlot({
                    id: currentSlot?.id ?? '',
                    label: currentSlot?.label ?? '',
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: {
                      ...currentSlot?.style,
                      align: (data.optionValue as CardSlotAlign) ?? 'left',
                    },
                  });
                }}
              >
                {CARD_ALIGN_OPTIONS.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field label="Font Size">
              <Dropdown
                value={currentSlot?.style?.fontSize ?? 'md'}
                selectedOptions={[currentSlot?.style?.fontSize ?? 'md']}
                onOptionSelect={(_ev, data) => {
                  setCurrentSlot({
                    id: currentSlot?.id ?? '',
                    label: currentSlot?.label ?? '',
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: {
                      ...currentSlot?.style,
                      fontSize: (data.optionValue as CardSlotFontSize) ?? 'md',
                    },
                  });
                }}
              >
                {CARD_FONT_SIZE_OPTIONS.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field label="Color (optional)">
              <Input
                value={currentSlot?.style?.color ?? ''}
                placeholder="#323130"
                onChange={(_, data) => {
                  setCurrentSlot({
                    id: currentSlot?.id ?? '',
                    label: currentSlot?.label ?? '',
                    order: currentSlot?.order ?? getNextSlotOrder(),
                    style: {
                      ...currentSlot?.style,
                      color: data.value,
                    },
                  });
                }}
              />
            </Field>

            <Divider />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Subtitle2Stronger>Fields in this Slot</Subtitle2Stronger>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="secondary"
                    icon={<AddRegular />}
                    disabled={disabled}
                  >
                    Add Field
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <div style={{ padding: '4px 8px', fontWeight: 600 }}>
                      Static Fields
                    </div>
                    {availableStaticFieldOptions
                      .filter(
                        (opt) =>
                          !slotFields.some(
                            (f) => f.isStatic && f.fieldKey === opt.key
                          )
                      )
                      .map((opt) => (
                        <MenuItem
                          key={opt.key}
                          onClick={() => handleAddFieldToSlot(opt.key, true)}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    <Divider />
                    <div style={{ padding: '4px 8px', fontWeight: 600 }}>
                      Custom Fields
                    </div>
                    {(metadata?.fields ?? [])
                      .filter(
                        (field) =>
                          CARD_SLOT_SUPPORTED_FIELD_TYPES.has(
                            field.fieldType
                          ) &&
                          !slotFields.some(
                            (f) => !f.isStatic && f.fieldKey === field.name
                          )
                      )
                      .map((field) => (
                        <MenuItem
                          key={field.name}
                          onClick={() =>
                            handleAddFieldToSlot(field.name, false)
                          }
                        >
                          {field.label || field.name}
                        </MenuItem>
                      ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>

            <Caption1>
              Use the arrow buttons to reorder. Fields are displayed in the
              order shown below.
            </Caption1>

            {slotFields.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  textAlign: 'center',
                  color: '#605e5c',
                  border: '1px dashed #8a8886',
                  borderRadius: 4,
                }}
              >
                No fields added yet. Click "Add Field" to get started.
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {slotFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e1dfdd',
                      borderRadius: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#fff',
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: '#605e5c',
                          minWidth: 24,
                        }}
                      >
                        {index + 1}.
                      </span>
                      <span>{field.label}</span>
                      {field.isStatic && (
                        <Badge appearance="filled" color="informative">
                          Static
                        </Badge>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowUpRegular />}
                        disabled={index === 0}
                        onClick={() => handleMoveFieldUp(index)}
                        title="Move up"
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowDownRegular />}
                        disabled={index === slotFields.length - 1}
                        onClick={() => handleMoveFieldDown(index)}
                        title="Move down"
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DeleteRegular />}
                        onClick={() => handleRemoveFieldFromSlot(field.id)}
                        title="Remove"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Divider />

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button
                appearance="primary"
                icon={<CheckmarkRegular />}
                onClick={handleSaveSlot}
                disabled={disabled}
              >
                Save Slot
              </Button>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={hideSlotEditorPanel}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DrawerBody>
      </OverlayDrawer>
    </Stack>
  );
};

export default CardDesignerPanel;
